import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import Layout from "@/components/Layout";
import HomePage from "@/pages/HomePage";
import DestinationPage from "@/pages/DestinationPage";
import ListingPage from "@/pages/ListingPage";
import DealsPage from "@/pages/DealsPage";
import HelpPage from "@/pages/HelpPage";
import LegalPage from "@/pages/LegalPage";
import ContactPage from "@/pages/ContactPage";
import NotFound from "@/pages/NotFound";
import { SLUGS, resolvePage, resolveHub, resolveLegacy, pagePath, hubs } from "@/data";
import HubPage from "@/pages/HubPage";
import { initAnalytics, bindAffiliateClickTracking, hasAnalyticsConsent } from "@/lib/analytics";

function StaticResolver() {
  const { lang, page } = useParams();
  const key = Object.keys(SLUGS).find((k) => SLUGS[k][lang] === page);
  if (!key) return <NotFound />;
  if (key === "carRental") return <ListingPage kind="carRental" />;
  if (key === "airports") return <ListingPage kind="airports" />;
  if (key === "deals") return <DealsPage />;
  if (key === "help") return <HelpPage />;
  if (key === "contact") return <ContactPage />;
  if (["about", "privacy", "cookies", "terms", "affiliate"].includes(key))
    return <LegalPage pageKey={key} />;
  return <NotFound />;
}

function DestinationRoute() {
  const { lang, cr, slug } = useParams();
  if (cr !== SLUGS.carRental[lang]) return <NotFound />;
  const hub = resolveHub(lang, slug);
  if (hub) return <HubPage hub={hub} />;
  if (!resolvePage(lang, slug)) return <NotFound />;
  return <DestinationPage />;
}

// Redirects pre-launch nested URLs (/en/car-rental/italy/rome/fiumicino-airport)
// to the flat canonical structure (/en/car-rental/rome-fiumicino).
function LegacyRedirect() {
  const { lang, cr, country, city, airport } = useParams();
  if (cr !== SLUGS.carRental[lang]) return <NotFound />;
  const page = resolveLegacy(lang, country, city, airport);
  if (!page) return <NotFound />;
  return <Navigate to={pagePath(lang, page)} replace />;
}

function Landing() {
  const lang =
    typeof navigator !== "undefined" && navigator.language && navigator.language.startsWith("it")
      ? "it"
      : "en";
  return <Navigate to={`/${lang}`} replace />;
}

function App() {
  useEffect(() => {
    if (hasAnalyticsConsent()) initAnalytics();
    bindAffiliateClickTracking();
  }, []);

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/:lang" element={<Layout />}>
          <Route index element={<HomePage />} />
          <Route path=":page" element={<StaticResolver />} />
          <Route path=":cr/:slug" element={<DestinationRoute />} />
          <Route path=":cr/:country/:city" element={<LegacyRedirect />} />
          <Route path=":cr/:country/:city/:airport" element={<LegacyRedirect />} />
          <Route path="*" element={<NotFound />} />
        </Route>
        <Route path="*" element={<Navigate to="/en" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
